// @flow
import axios from 'axios'
import { reportError } from './bugSnag'
import { isOnline, isNetworkError } from './networkStatus'

// Create axios instance with default config
const axiosInstance = axios.create({
  withCredentials: true,
  timeout: 30000, // 30 second timeout
  responseType: 'json'
})

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 5000, // 5 seconds
  backoffMultiplier: 2
}

// Error types that should not be reported to Bugsnag
const SILENT_ERROR_CODES = [
  'ECONNABORTED', // Timeout
  'ERR_NETWORK', // Network error
  'ERR_INTERNET_DISCONNECTED', // No internet
  'ERR_CANCELED' // Request canceled
]

// Check if error should be retried
function shouldRetry (error: any, retryCount: number): boolean {
  if (retryCount >= RETRY_CONFIG.maxRetries) {
    return false
  }

  // Don't retry if offline
  if (!isOnline()) {
    return false
  }

  // Retry on network errors
  if (error.code && SILENT_ERROR_CODES.includes(error.code)) {
    return true
  }

  // Retry on timeout
  if (error.code === 'ECONNABORTED') {
    return true
  }

  // Retry on any network error
  if (isNetworkError(error)) {
    return true
  }

  // Retry on 5xx server errors
  if (error.response && error.response.status >= 500) {
    return true
  }

  // Don't retry on client errors (4xx)
  if (error.response && error.response.status >= 400 && error.response.status < 500) {
    return false
  }

  return false
}

// Calculate retry delay with exponential backoff
function getRetryDelay (retryCount: number): number {
  const delay = Math.min(
    RETRY_CONFIG.initialDelay * Math.pow(RETRY_CONFIG.backoffMultiplier, retryCount),
    RETRY_CONFIG.maxDelay
  )
  // Add jitter to prevent thundering herd
  return delay + Math.random() * 1000
}

// Check if we should report error to Bugsnag
function shouldReportError (error: any, retryCount: number): boolean {
  // Don't report if we haven't exhausted retries
  if (retryCount < RETRY_CONFIG.maxRetries) {
    return false
  }

  // Don't report if user is offline
  if (!isOnline()) {
    return false
  }

  // Don't report common network errors
  if (error.code && SILENT_ERROR_CODES.includes(error.code)) {
    return false
  }

  // Don't report timeout errors
  if (error.code === 'ECONNABORTED') {
    return false
  }

  // Don't report any network errors
  if (isNetworkError(error)) {
    return false
  }

  // Report other errors
  return true
}

// Enhanced request function with retry logic
export async function makeRequest (config: any, options: ?{ skipRetry?: boolean } = {}): Promise<any> {
  let lastError
  let retryCount = 0

  while (retryCount <= RETRY_CONFIG.maxRetries) {
    try {
      const response = await axiosInstance(config)
      return response.data
    } catch (error) {
      lastError = error

      // Check if we should retry
      if (!options?.skipRetry && shouldRetry(error, retryCount)) {
        retryCount++
        const delay = getRetryDelay(retryCount - 1)

        // Log retry attempt in development
        if (process.env.NODE_ENV === 'development') {
          console.log(`Retrying request to ${config.url} (attempt ${retryCount}/${RETRY_CONFIG.maxRetries}) after ${Math.round(delay)}ms`)
        }

        // Wait before retrying
        await new Promise((resolve: () => void): void => { setTimeout(resolve, delay) })
        continue
      }

      // If we shouldn't retry or have exhausted retries, handle the error
      const shouldReport = shouldReportError(error, retryCount)

      if (shouldReport) {
        // Extract error details for reporting
        const errorDetails = {
          url: config.url,
          method: config.method || 'GET',
          retryCount,
          errorCode: error.code,
          errorMessage: error.message,
          responseStatus: error.response?.status,
          responseData: error.response?.data
        }

        // Report to Bugsnag with context
        reportError(new Error(`API Error: ${error.message}`), (event: any): void => {
          event.addMetadata('api', errorDetails)
          event.addMetadata('request', {
            config: {
              ...config,
              // Don't include sensitive headers
              headers: undefined
            }
          })
        })
      }

      // Re-throw the error
      throw error
    }
  }

  // This should never be reached, but TypeScript needs it
  throw lastError
}

// Convenience methods
export const get = (url: string, config?: any): Promise<any> =>
  makeRequest({ ...config, method: 'GET', url })

export const post = (url: string, data?: any, config?: any): Promise<any> =>
  makeRequest({ ...config, method: 'POST', url, data })

export const put = (url: string, data?: any, config?: any): Promise<any> =>
  makeRequest({ ...config, method: 'PUT', url, data })

export const del = (url: string, config?: any): Promise<any> =>
  makeRequest({ ...config, method: 'DELETE', url })

export default {
  makeRequest,
  get,
  post,
  put,
  delete: del
}
